import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

function DataConsistencyFix() {
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(true);
  const [problemProducts, setProblemProducts] = useState([]);
  const [results, setResults] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [summary, setSummary] = useState(null);

  // 페이지 로드 시 동적으로 문제 제품 스캔
  useEffect(() => {
    scanForIssues();
  }, []);

  const scanForIssues = async () => {
    setScanning(true);
    setProblemProducts([]);
    setResults([]);
    setSelectedProducts(new Set());

    try {
      const snapshot = await getDocs(collection(db, 'inventory'));
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const foundIssues = [];

      items.forEach(item => {
        const itemIssues = [];
        const quantity = Number(item.quantity) || 1;
        const skuCount = item.skuLocations?.length || 0;
        const mainLocation = item.location;

        // 1. location과 skuLocations 불일치 확인
        if (item.skuLocations && item.skuLocations.length > 0) {
          const allSkusInSameLocation = item.skuLocations.every(
            sku => sku.location === mainLocation
          );

          if (!allSkusInSameLocation) {
            const mismatchedSkus = item.skuLocations.filter(sku => sku.location !== mainLocation);
            itemIssues.push({
              type: 'LOCATION_MISMATCH',
              description: `SKU 위치 불일치 (${mismatchedSkus.length}개)`,
              fix: `모든 SKU location을 "${mainLocation}"으로 동기화`,
              fixType: 'sync_location'
            });
          }
        }

        // 2. productType이 unique인데 quantity > 1
        if (item.productType === 'unique' && quantity > 1) {
          itemIssues.push({
            type: 'TYPE_QUANTITY_MISMATCH',
            description: `일품 제품인데 수량이 ${quantity}개`,
            fix: `quantity를 1로 변경`,
            fixType: 'set_quantity_1'
          });
        }

        // 3. productType이 unique인데 SKU > 1
        if (item.productType === 'unique' && skuCount > 1) {
          itemIssues.push({
            type: 'TYPE_SKU_MISMATCH',
            description: `일품 제품인데 SKU가 ${skuCount}개`,
            fix: `SKU를 1개만 유지`,
            fixType: 'keep_one_sku'
          });
        }

        // 4. quantity와 skuLocations 개수 불일치 (bulk 제품)
        if (skuCount > 0 && quantity !== skuCount && item.productType !== 'unique') {
          itemIssues.push({
            type: 'QUANTITY_SKU_COUNT_MISMATCH',
            description: `수량(${quantity})과 SKU 개수(${skuCount}) 불일치`,
            fix: `quantity를 ${skuCount}로 변경 (SKU 기준)`,
            fixType: 'sync_quantity_to_sku'
          });
        }

        if (itemIssues.length > 0) {
          foundIssues.push({
            id: item.id,
            name: item.productName,
            barcode: item.barcodeId,
            location: mainLocation,
            quantity,
            productType: item.productType,
            skuCount,
            skuLocations: item.skuLocations || [],
            issues: itemIssues
          });
        }
      });

      setProblemProducts(foundIssues);
      setSummary({
        total: items.length,
        issueCount: foundIssues.length
      });

    } catch (error) {
      console.error('스캔 중 오류:', error);
      alert(`오류 발생: ${error.message}`);
    } finally {
      setScanning(false);
    }
  };

  const fixProduct = async (product) => {
    const docRef = doc(db, 'inventory', product.id);
    let updateData = { updatedAt: serverTimestamp() };
    let fixDescriptions = [];

    // 각 문제 유형에 대한 수정 적용
    for (const issue of product.issues) {
      switch (issue.fixType) {
        case 'sync_location':
          // 모든 SKU location을 main location으로 동기화
          updateData.skuLocations = product.skuLocations.map(sku => ({
            ...sku,
            location: product.location
          }));
          fixDescriptions.push(`SKU 위치 → ${product.location}`);
          break;

        case 'set_quantity_1':
          // quantity를 1로 설정
          updateData.quantity = 1;
          fixDescriptions.push(`수량 → 1`);
          break;

        case 'keep_one_sku':
          // SKU를 1개만 유지
          updateData.skuLocations = product.skuLocations.length > 0
            ? [product.skuLocations[0]]
            : [];
          updateData.quantity = 1;
          fixDescriptions.push(`SKU → 1개만 유지`);
          break;

        case 'sync_quantity_to_sku':
          // quantity를 SKU 개수와 동기화
          updateData.quantity = product.skuCount;
          fixDescriptions.push(`수량 → ${product.skuCount}`);
          break;

        default:
          break;
      }
    }

    try {
      await updateDoc(docRef, updateData);
      return {
        success: true,
        description: fixDescriptions.join(', '),
        before: {
          quantity: product.quantity,
          skuCount: product.skuCount,
          location: product.location
        },
        after: {
          quantity: updateData.quantity || product.quantity,
          skuCount: updateData.skuLocations ? updateData.skuLocations.length : product.skuCount,
          location: product.location
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const executeFixAll = async () => {
    if (selectedProducts.size === 0) {
      alert('수정할 제품을 선택해주세요.');
      return;
    }

    setLoading(true);
    const fixResults = [];

    const productsToFix = problemProducts.filter(p => selectedProducts.has(p.id));

    for (const product of productsToFix) {
      const result = await fixProduct(product);
      fixResults.push({
        id: product.id,
        name: product.name,
        barcode: product.barcode,
        ...result
      });

      await new Promise(resolve => setTimeout(resolve, 300));
    }

    setResults(fixResults);

    // 수정 성공한 제품들을 problemProducts에서 제거
    const successfulIds = fixResults.filter(r => r.success).map(r => r.id);
    if (successfulIds.length > 0) {
      setProblemProducts(prev => prev.filter(p => !successfulIds.includes(p.id)));
      setSelectedProducts(prev => {
        const newSet = new Set(prev);
        successfulIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    }

    setLoading(false);
    setShowConfirm(false);
  };

  const toggleProduct = (productId) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedProducts.size === problemProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(problemProducts.map(p => p.id)));
    }
  };

  const executeSingleFix = async (product) => {
    setLoading(true);
    const result = await fixProduct(product);

    setResults(prev => {
      const existingIndex = prev.findIndex(r => r.id === product.id);
      const newResult = {
        id: product.id,
        name: product.name,
        barcode: product.barcode,
        ...result
      };

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = newResult;
        return updated;
      } else {
        return [...prev, newResult];
      }
    });

    if (result.success) {
      setProblemProducts(prev => prev.filter(p => p.id !== product.id));
      setSelectedProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(product.id);
        return newSet;
      });
    }

    setLoading(false);

    if (result.success) {
      alert(`${product.name} 수정 완료!\n\n${result.description}`);
    } else {
      alert(`${product.name} 수정 실패!\n\n${result.error}`);
    }
  };

  const getIssueTypeBadge = (type) => {
    const badges = {
      'LOCATION_MISMATCH': 'bg-yellow-100 text-yellow-800',
      'TYPE_QUANTITY_MISMATCH': 'bg-red-100 text-red-800',
      'TYPE_SKU_MISMATCH': 'bg-red-100 text-red-800',
      'QUANTITY_SKU_COUNT_MISMATCH': 'bg-orange-100 text-orange-800'
    };
    return badges[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold mb-4 border-b-2 border-red-500 pb-2">
          🔧 데이터 일관성 자동 수정
        </h1>

        {scanning ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500 mx-auto mb-4"></div>
            <p className="text-gray-600">재고 데이터 스캔 중...</p>
          </div>
        ) : problemProducts.length === 0 ? (
          <div className="bg-green-50 border-l-4 border-green-500 p-6 text-center">
            <h3 className="text-xl font-bold text-green-700 mb-2">✅ 모든 제품이 정상입니다!</h3>
            <p className="text-green-600">수정이 필요한 제품이 없습니다.</p>
            {summary && (
              <p className="text-sm text-gray-500 mt-2">
                총 {summary.total}개 제품 검사 완료
              </p>
            )}
            <button
              onClick={scanForIssues}
              className="mt-4 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded"
            >
              다시 검사
            </button>
          </div>
        ) : (
          <>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6">
              <h3 className="font-bold text-lg mb-2">⚠️ 주의사항</h3>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>이 작업은 되돌릴 수 없습니다.</li>
                <li>
                  총 {summary?.total || 0}개 중 <strong className="text-red-600">{problemProducts.length}개</strong> 수정 필요,{' '}
                  <strong className="text-blue-600">{selectedProducts.size}개</strong> 선택됨
                </li>
                <li>수정 전에 반드시 아래 내역을 확인하세요.</li>
              </ul>
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">수정할 제품 목록 ({problemProducts.length}개)</h2>
                <div className="flex gap-2">
                  <button
                    onClick={scanForIssues}
                    className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded text-sm"
                  >
                    새로고침
                  </button>
                  <button
                    onClick={toggleSelectAll}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm"
                  >
                    {selectedProducts.size === problemProducts.length ? '전체 해제' : '전체 선택'}
                  </button>
                </div>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {problemProducts.map((product, index) => {
                  const resultForProduct = results.find(r => r.id === product.id);
                  return (
                    <div
                      key={product.id}
                      className={`p-3 rounded border ${
                        selectedProducts.has(product.id) ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'
                      } ${resultForProduct?.success ? 'border-green-500 bg-green-50' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedProducts.has(product.id)}
                          onChange={() => toggleProduct(product.id)}
                          className="mt-1 w-5 h-5 rounded"
                        />
                        <div className="flex-1">
                          <div className="font-semibold">
                            {index + 1}. {product.name} ({product.barcode})
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            <a
                              href={`/inventory/${product.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {product.id}
                            </a>
                            <span className="ml-2">| 위치: {product.location} | 수량: {product.quantity} | SKU: {product.skuCount}개</span>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {product.issues.map((issue, i) => (
                              <span key={i} className={`px-2 py-1 rounded text-xs ${getIssueTypeBadge(issue.type)}`}>
                                {issue.type}
                              </span>
                            ))}
                          </div>
                          <div className="text-sm mt-2 text-green-700">
                            → {product.issues.map(i => i.fix).join(', ')}
                          </div>
                          {resultForProduct && (
                            <div className={`text-sm mt-2 font-medium ${resultForProduct.success ? 'text-green-600' : 'text-red-600'}`}>
                              {resultForProduct.success ? `✅ 수정됨: ${resultForProduct.description}` : `❌ 실패: ${resultForProduct.error}`}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => executeSingleFix(product)}
                          disabled={loading}
                          className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded text-sm disabled:bg-gray-400"
                        >
                          개별 수정
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {!showConfirm && (
              <div className="flex gap-4">
                <button
                  onClick={() => setShowConfirm(true)}
                  disabled={selectedProducts.size === 0}
                  className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-bold disabled:bg-gray-400"
                >
                  선택된 {selectedProducts.size}개 수정 시작
                </button>
                {results.length > 0 && (
                  <button
                    onClick={() => setResults([])}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg"
                  >
                    결과 초기화
                  </button>
                )}
              </div>
            )}

            {showConfirm && (
              <div className="bg-red-50 border-2 border-red-500 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-red-700 mb-4">
                  정말 실행하시겠습니까?
                </h3>
                <p className="mb-4">
                  선택된 <strong>{selectedProducts.size}개</strong> 제품의 데이터가 영구적으로 수정됩니다.
                </p>
                <div className="flex gap-4">
                  <button
                    onClick={executeFixAll}
                    disabled={loading}
                    className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg disabled:bg-gray-400"
                  >
                    {loading ? '수정 중...' : '확인 - 수정 실행'}
                  </button>
                  <button
                    onClick={() => setShowConfirm(false)}
                    disabled={loading}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg disabled:bg-gray-400"
                  >
                    취소
                  </button>
                </div>
              </div>
            )}

            {results.length > 0 && (
              <div className="mt-6">
                <h2 className="text-xl font-bold mb-4">수정 결과</h2>
                <div className="space-y-3">
                  {results.map((result) => (
                    <div
                      key={result.id}
                      className={`p-4 rounded border-l-4 ${
                        result.success
                          ? 'bg-green-50 border-green-500'
                          : 'bg-red-50 border-red-500'
                      }`}
                    >
                      <div className="font-semibold">
                        {result.success ? '✅' : '❌'} {result.name} ({result.barcode})
                      </div>
                      {result.success ? (
                        <div className="text-sm mt-2">
                          <div className="text-gray-600">수정 내용: {result.description}</div>
                          <div className="grid grid-cols-2 gap-4 mt-2">
                            <div>
                              <div className="font-semibold text-red-600">수정 전:</div>
                              <div>수량: {result.before.quantity}</div>
                              <div>SKU: {result.before.skuCount}개</div>
                            </div>
                            <div>
                              <div className="font-semibold text-green-600">수정 후:</div>
                              <div>수량: {result.after.quantity}</div>
                              <div>SKU: {result.after.skuCount}개</div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-red-600 mt-1">
                          오류: {result.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4">
                  <h3 className="font-bold text-lg mb-2">완료!</h3>
                  <p>
                    총 {results.length}개 중{' '}
                    <strong className="text-green-600">
                      {results.filter((r) => r.success).length}개 성공
                    </strong>
                    {results.filter((r) => !r.success).length > 0 && (
                      <>
                        ,{' '}
                        <strong className="text-red-600">
                          {results.filter((r) => !r.success).length}개 실패
                        </strong>
                      </>
                    )}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DataConsistencyFix;
